import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { CheckCircle, FileDownload } from '@mui/icons-material';

import React from 'react';

import { buildAttendance, downloadCsv, downloadPdf, formatDuration, formatLogRange, STILL_IN } from './attendance';

const NAME_WIDTH = 180;

export const AttendanceReport = ({ events, patrollers, shiftsByEvent }) => {
    const attendance = React.useMemo(
        () => buildAttendance(patrollers, events, shiftsByEvent),
        [ patrollers, events, shiftsByEvent ]
    );

    const nameCellSx = {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        minWidth: NAME_WIDTH,
        maxWidth: NAME_WIDTH,
        fontWeight: 'bold',
        backgroundColor: 'inherit'
    };

    const headCellSx = {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        fontWeight: 'bold',
        whiteSpace: 'nowrap'
    };

    return (
        <Box sx={ { mb: 3 } }>
            <Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 } }>
                <Box>
                    <Typography variant="h6">Attendance Across Events</Typography>
                    <Typography variant="body2" color="text.secondary">
                        { attendance.rows.length } patroller{ attendance.rows.length === 1 ? '' : 's' } - { attendance.columns.length } event{ attendance.columns.length === 1 ? '' : 's' }
                    </Typography>
                </Box>

                <Box sx={ { display: 'flex', gap: 1 } }>
                    <Button variant="contained" startIcon={ <FileDownload /> } onClick={ () => downloadCsv(attendance) }>Download CSV</Button>
                    <Button variant="contained" startIcon={ <FileDownload /> } onClick={ () => downloadPdf(attendance) }>Download PDF</Button>
                </Box>
            </Box>

            <TableContainer component={ Paper } sx={ { maxHeight: '70vh', maxWidth: '100%' } }>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={ { ...headCellSx, position: 'sticky', left: 0, zIndex: 3, minWidth: NAME_WIDTH } }>Patroller</TableCell>
                            { attendance.columns.map((c, i) => <TableCell key={ i } align="center" sx={ headCellSx }>{ c }</TableCell>) }
                            <TableCell align="center" sx={ headCellSx }>Total Time</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        { attendance.rows.map(r => (
                            <TableRow
                                key={ r.id }
                                hover
                                sx={ {
                                    backgroundColor: 'background.paper',
                                    '&:nth-of-type(even)': { backgroundColor: 'action.hover' },
                                    '&:hover': { backgroundColor: 'action.selected' }
                                } }
                            >
                                <TableCell sx={ nameCellSx }>{ r.name }</TableCell>

                                { r.details.map((d, i) => (
                                    <TableCell key={ i } align="center">
                                        { (d.ms > 0 || d.open) ? (
                                            <Tooltip
                                                title={ <Box>{ d.logs.map((l, k) => <div key={ k }>{ formatLogRange(l) }</div>) }</Box> }
                                                arrow
                                            >
                                            <Box sx={ { display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 } }>
                                                { d.ms > 0 && (
                                                    <Chip
                                                        size="small"
                                                        icon={ <CheckCircle /> }
                                                        label={ formatDuration(d.ms) }
                                                        sx={ { backgroundColor: 'rgba(76, 175, 80, 0.18)', color: 'success.dark', '& .MuiChip-icon': { color: 'success.main' } } }
                                                    />
                                                ) }
                                                { d.open && (
                                                    <Typography variant="caption" sx={ { color: 'warning.main', fontWeight: 'bold' } }>{ STILL_IN }</Typography>
                                                ) }
                                            </Box>
                                            </Tooltip>
                                        ) : (
                                            <Typography component="span" sx={ { color: 'text.disabled' } }>&ndash;</Typography>
                                        ) }
                                    </TableCell>
                                )) }

                                <TableCell align="center">
                                    <Chip
                                        size="small"
                                        label={ r.totalMs > 0 ? r.total : '-' }
                                        sx={ r.totalMs === 0
                                            ? { fontWeight: 'bold', backgroundColor: 'rgba(244, 67, 54, 0.15)', color: 'error.main' }
                                            : { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'primary.contrastText' } }
                                    />
                                </TableCell>
                            </TableRow>
                        )) }
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
